import React, { useState } from "react";
import PortfolioPageShell from "../components/portfolio/PortfolioPageShell";

const MODES = {
  inference: {
    label: "LLM Inference",
    title: "Serving LLMs Under Production SLOs",
    summary:
      "I treat inference as a distributed systems problem, not a model problem. The model is one component in a graph of schedulers, caches, and queues.",
    points: [
      "Built multi-tenant LLM serving on vLLM and TensorRT-LLM with paged attention, continuous batching, and speculative decoding to keep p99 latency under SLA.",
      "Tuned KV-cache reuse, prefix caching, and dynamic batching to push throughput per GPU 3–5x without changing the model.",
      "Quantised production models to FP8, AWQ, and GPTQ; profiled with NVIDIA Nsight and Triton metrics to find the real bottleneck (it is almost never the matmul).",
    ],
  },
  distributed: {
    label: "Distributed Systems",
    title: "Backends That Hold Up Under Real Load",
    summary:
      "Most of what makes an AI product work is the plumbing: queues, retries, sharding, idempotency, and the boring discipline of designing for failure.",
    points: [
      "Designed event-driven backends on Kafka and Redis Streams with at-least-once delivery, idempotent consumers, and saga-style compensation for multi-step agent workflows.",
      "Sharded vector and relational stores (Milvus, Postgres, Qdrant) across regions; built read-replica routing and cache hierarchies to keep tail latency flat under burst traffic.",
      "Ran services on Kubernetes with HPA, PodDisruptionBudgets, and gRPC mesh; wired OpenTelemetry traces end-to-end so a slow request can be explained in minutes, not days.",
    ],
  },
  agentic: {
    label: "Agentic Systems",
    title: "Multi-Agent Systems for Real Workflows",
    summary:
      "I build agentic systems that survive enterprise transactions, not eval-set demos. The contract is recoverable failures and clean tool handoffs.",
    points: [
      "Architected fault-tolerant multi-agent orchestrations over LangGraph and MCP with bounded loops, durable checkpoints, and explicit cancellation semantics.",
      "Designed tool registries with schema validation, rate limits, and per-tool circuit breakers so a flaky downstream cannot take the agent down.",
      "Built shared agent memory (short-term, episodic, semantic) backed by Postgres + Redis + Qdrant, with PII masking and HMAC-signed audit trails by default.",
    ],
  },
  training: {
    label: "Training & Eval",
    title: "Post-Training, Quantisation, and Eval Pipelines",
    summary:
      "Most production wins come from the system around the weights. When the weights do need to move, I move them with discipline.",
    points: [
      "Fine-tuned domain LLMs with LoRA, QLoRA, DPO, and GRPO on multi-GPU (DeepSpeed ZeRO-3, FSDP) with mixed precision and gradient checkpointing.",
      "Built end-to-end eval harnesses (Inspect AI, lm-eval-harness, custom) with paired bootstrap, MDE, and CI gates so model promotions are decisions, not vibes.",
      "Shipped guardrails for PII, prompt injection, toxicity, and unsafe tool calls before any traffic; red-team suites run in CI on every model bump.",
    ],
  },
};

const MODE_KEYS = Object.keys(MODES);

const SHADOW_BIO = "5px 5px 0 0 #FBBF24";
const SHADOW_MODE = "5px 5px 0 0 #7DD3FC";

export default function PortfolioAbout() {
  const [active, setActive] = useState("inference");
  const mode = MODES[active];

  return (
    <PortfolioPageShell
      tag="ABOUT"
      title="WHO I AM"
      subtitle="AI SYSTEMS ENGINEER"
      description="LLMs • Distributed Systems • Backend Engineering • Scalable AI Products. I design and ship the infrastructure that turns models into products: inference serving, agentic backends, eval gates, and the distributed systems underneath."
    >
      <article
        className="bg-white border-[3px] border-[#0A0A0A] p-6 md:p-10 mb-8 grid grid-cols-1 md:grid-cols-[280px_1fr] gap-8 items-start"
        style={{ boxShadow: SHADOW_BIO }}
      >
        <div className="border-2 border-[#0A0A0A] bg-[#F5F5F0] aspect-square overflow-hidden">
          <img
            src="/images/portfolio/avataar.png"
            alt="Vikram Dev"
            className="w-full h-full object-cover [image-rendering:auto]"
          />
        </div>
        <div className="font-terminal text-sm md:text-base text-[#0A0A0A]/85 leading-relaxed space-y-5">
          <p>
            I'm Vikram Dev, an AI Systems Engineer based in Gurgaon, India. I work at the
            intersection of LLMs, distributed systems, and backend engineering, with a focus
            on the parts that decide whether a model becomes a product that scales: inference
            serving, agentic orchestration, data and storage layers, and the observability
            stack underneath.
          </p>
          <p>
            Across PwC, Cars24, and FN Mathlogic, I have shipped production agentic systems,
            credit-risk pipelines, and ML governance workflows; on the side, I build open-source
            tools for the LLM stack (mcpolish, memnex, evalsig, CiteForge). I own the full path
            from system design and capacity planning to inference tuning, eval harnesses, and
            the on-call runbook.
          </p>
          <p>
            What I care about: agents that survive production load, services that degrade
            gracefully, evals that catch the failure modes nobody else looked for, and code
            that another engineer can read at 2 AM during an incident.
          </p>
        </div>
      </article>

      <article
        className="bg-white border-[3px] border-[#0A0A0A] p-6 md:p-10"
        style={{ boxShadow: SHADOW_MODE }}
      >
        <div
          className="flex flex-wrap gap-2 mb-6 border-b-2 border-[#0A0A0A]/15 pb-4"
          role="tablist"
          aria-label="Focus areas"
        >
          {MODE_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setActive(key)}
              role="tab"
              aria-selected={active === key}
              className={`font-arcade text-base uppercase tracking-wider px-4 py-2 border-2 transition-colors duration-150 ${
                active === key
                  ? "bg-[#7DD3FC] text-black border-[#7DD3FC]"
                  : "bg-transparent text-[#0A0A0A] border-[#0A0A0A] hover:bg-[#7DD3FC]/30"
              }`}
            >
              {MODES[key].label}
            </button>
          ))}
        </div>

        <h2 className="font-pixel text-base md:text-lg text-[#0A0A0A] leading-snug mb-3">
          {mode.title}
        </h2>
        <p className="font-terminal text-sm md:text-base text-[#0A0A0A]/85 leading-relaxed mb-5">
          {mode.summary}
        </p>
        <ul className="space-y-3">
          {mode.points.map((p) => (
            <li
              key={p}
              className="font-terminal text-sm md:text-base text-[#0A0A0A]/80 flex items-start gap-2 leading-relaxed"
            >
              <span className="text-[#0369A1] mt-0.5 shrink-0">&gt;</span>
              {p}
            </li>
          ))}
        </ul>
      </article>
    </PortfolioPageShell>
  );
}
